/**
 * The spreadsheet a Help & Tour "uploads" on the Bulk Upload screen, and the
 * staged upload the sample layer answers with.
 *
 * The page posts the picked file to the user upload API, which the tour's
 * sample layer intercepts — so the file only has to exist. It is still built
 * from the shared demo employees with the real template's columns, and the
 * same rows back the preview's `/details` response, so what the tour shows
 * matches what it "uploaded".
 */

import ExcelJS from 'exceljs';

import {
  BULK_USER_EXCEL_COLUMNS,
  BULK_USER_EXCEL_HEADERS,
  BULK_USER_FIELD_DESCRIPTIONS,
  UserUploadDetail,
  UserUploadFile,
} from '@/models';

import { TOUR_EMPLOYEES } from './tour-sample-people';

/** The file name the tour's upload appears under. */
export const TOUR_BULK_UPLOAD_FILE_NAME = 'tour-sample-user-upload.xlsx';

/**
 * One spreadsheet row per demo employee, keyed by the real column names.
 *
 * Only the mandatory fields are filled: User ID, Name, Login Email, Employee
 * ID, Company, Entity, Payroll, Crediting Account (CDP or DA) and User Status
 * (Active / Inactive / Disabled). The optional date columns are left blank
 * rather than invented, so the preview shows a clean validation pass.
 */
export const buildTourBulkUploadRows = (): Record<string, string>[] =>
  TOUR_EMPLOYEES.map((employee, index) => ({
    userId: `TOUR-USR-${String(index + 1).padStart(3, '0')}`,
    name: employee.fullName,
    loginEmail: employee.email,
    mobileNumber: `6555${String(1000 + index)}`,
    country: employee.country,
    employeeId: employee.employeeId,
    company: employee.company,
    entity: employee.company,
    payroll: `${employee.country} Payroll`,
    department: employee.department,
    designation: employee.designation,
    jobGrade: employee.jobGrade,
    costCentre: 'CC-100',
    division: 'Group',
    creditingAccount: 'CDP',
    cdpAccountName: employee.fullName,
    // The validator requires CDP account numbers in XXXX-XXXX-XXXX form.
    cdpAccountNumber: `1680-0001-${String(2345 + index)}`,
    userStatus: employee.employmentStatus === 'Active' ? 'Active' : 'Inactive',
    remarks: 'Help & Tours sample row',
  }));

/**
 * Build the sample workbook as a `File`, ready to hand to the page's own
 * upload handler.
 *
 * The layout mirrors the upload template: a header row, the field
 * description row, then the data.
 */
export const buildTourBulkUploadFile = async (): Promise<File> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('User Bulk Upload');

  worksheet.columns = BULK_USER_EXCEL_HEADERS.map((header, index) => ({
    header,
    key: BULK_USER_EXCEL_COLUMNS[index],
    width: 25,
  }));

  worksheet.addRow([...BULK_USER_FIELD_DESCRIPTIONS]);
  buildTourBulkUploadRows().forEach(row => worksheet.addRow(row));

  const buffer = await workbook.xlsx.writeBuffer();
  return new File([buffer], TOUR_BULK_UPLOAD_FILE_NAME, {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

/** The staged upload every tour upload / re-upload resolves to. */
export const TOUR_USER_UPLOAD_FILE_ID = 9801;

/** The staged rows, as the upload API's `/details` returns them. */
export const buildTourUserUploadDetails = (): UserUploadDetail[] =>
  buildTourBulkUploadRows().map((row, index) => ({
    id: index + 1,
    status: 'Success',
    isExcluded: false,
    errorMessages: null,
    createdUserId: null,
    userId: row.userId,
    fullName: row.name,
    email: row.loginEmail,
    mobileNumber: row.mobileNumber,
    country: row.country,
    employeeId: row.employeeId,
    company: row.company,
    entity: row.entity,
    payroll: row.payroll,
    department: row.department,
    designation: row.designation,
    jobGrade: row.jobGrade,
    costCentre: row.costCentre,
    division: row.division,
    cdpAccountName: row.cdpAccountName,
    cdpAccountNumber: row.cdpAccountNumber,
    creditingAccountType: row.creditingAccount,
    userRole: 'Employee',
    userStatus: row.userStatus,
    remarks: row.remarks,
  }));

export const TOUR_USER_UPLOAD_FILE: UserUploadFile = Object.freeze({
  fileId: TOUR_USER_UPLOAD_FILE_ID,
  fileName: TOUR_BULK_UPLOAD_FILE_NAME,
  totalRecords: TOUR_EMPLOYEES.length,
  readyForUploadCount: TOUR_EMPLOYEES.length,
  exceptionCount: 0,
  excludedCount: 0,
  createdUserCount: 0,
  status: 'Draft',
  workFlowActionId: 13,
  workflowActionName: 'UserUploadDraft',
  createdById: 0,
  uploadedBy: 'Tour Demo',
  uploadedAt: '2026-01-05T09:30:00',
  approvedBy: null,
  approvedAt: null,
  makerRemarks: null,
  checkerRemarks: null,
});
